'use client';

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { jobService } from '@/services/jobService';
import JobCard from './JobCard';
import JobFilters from './JobFilters';
import JobSortControls from './JobSortControls';
import AdZone from './AdZone';
import RegistrationModal from '@/components/common/RegistrationModal';
import Icon from '@/components/ui/AppIcon';

interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  salary: string;
  employmentType: string;
  postedDate: string;
  deadline: string;
  description: string;
  requirements: string[];
  benefits: string[];
  companyLogo: string;
  companyLogoAlt: string;
  isFeatured: boolean;
  isUrgent: boolean;
  applicationUrl?: string;
  contactEmail?: string;
  experienceLevel: string;
}

const JobsBoardInteractive = () => {
  const [isClient, setIsClient] = useState(false);
  const { isAuthenticated } = useAuth();
  const [isHydrated, setIsHydrated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [jobsData, setJobsData] = useState<Job[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('All Locations');
  const [jobTypeFilter, setJobTypeFilter] = useState('All Types');
  const [experienceFilter, setExperienceFilter] = useState('All Levels');
  const [salaryFilter, setSalaryFilter] = useState('All Ranges');
  const [sortBy, setSortBy] = useState('date');
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
  const [pendingJobId, setPendingJobId] = useState<string | null>(null);

  // CRITICAL FIX: Move loadJobsData BEFORE useEffect to avoid TDZ error
  const loadJobsData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const { data, error: fetchError } = await jobService.getAllApprovedJobs();
      
      if (fetchError) {
        setError('Failed to load job listings. Please try again later.');
        setIsLoading(false);
        return;
      }
      
      // Transform Supabase data to match Job interface
      const transformedData: Job[] = (data || []).map((job: any) => {
        const salaryMin = job.salary_min || 0;
        const salaryMax = job.salary_max || 0;
        const currency = job.salary_currency || 'USD';
        
        return {
          id: job.id,
          title: job.title,
          company: job.companies?.name || 'Company Name',
          location: job.location || 'Location Not Specified',
          salary: salaryMin && salaryMax 
            ? `$${salaryMin.toLocaleString()} - $${salaryMax.toLocaleString()}/${currency === 'USD' ? 'year' : 'month'}` 
            : 'Salary Not Disclosed',
          employmentType: job.job_type || 'full-time',
          postedDate: job.created_at,
          deadline: job.expires_at || '',
          description: job.description || '',
          requirements: job.requirements ? job.requirements.split('\n').filter((r: string) => r.trim()) : [],
          benefits: job.benefits ? job.benefits.split('\n').filter((b: string) => b.trim()) : [],
          companyLogo: job.companies?.logo_url || '/assets/images/no_image.png',
          companyLogoAlt: `${job.companies?.name || 'Company'} logo`,
          isFeatured: job.featured || false,
          isUrgent: false, // Can be determined by expires_at proximity
          experienceLevel: job.experience_level || 'entry',
          contactEmail: undefined, // Not stored in jobs table directly
          applicationUrl: undefined
        };
      });
      
      setJobsData(transformedData);
      setIsLoading(false);
    } catch (err: any) {
      setError(err?.message || 'An unexpected error occurred');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsClient(true);
    setIsHydrated(true);
    loadJobsData();
  }, []);

  // Add early return for server-side rendering
  if (!isClient) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded mb-4"></div>
          <div className="h-32 bg-muted rounded mb-4"></div>
          <div className="h-32 bg-muted rounded mb-4"></div>
          <div className="h-32 bg-muted rounded mb-4"></div>
        </div>
      </div>
    );
  }

  const handleApplyClick = (jobId: string) => {
    if (!isAuthenticated) {
      setPendingJobId(jobId);
      setIsRegistrationModalOpen(true);
      return;
    }

    // Handle authenticated apply action
    const job = jobsData.find((j) => j.id === jobId);
    if (job?.applicationUrl) {
      window.open(job.applicationUrl, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = `/job-detail?id=${jobId}#apply`;
    }
  };

  const handleRegistrationSuccess = () => {
    // Execute the pending job application after successful registration
    if (pendingJobId) {
      const job = jobsData.find((j) => j.id === pendingJobId);
      if (job?.applicationUrl) {
        window.open(job.applicationUrl, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = `/job-detail?id=${pendingJobId}#apply`;
      }
    }

    // Clear pending action
    setPendingJobId(null);
  };

  // Filter and sort jobs
  const filteredJobs = jobsData.filter((job) => {
    const matchesSearch = searchTerm === '' ||
    job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesLocation = locationFilter === 'All Locations' ||
    job.location.includes(locationFilter);

    const matchesJobType = jobTypeFilter === 'All Types' ||
    job.employmentType === jobTypeFilter;

    const matchesExperience = experienceFilter === 'All Levels' ||
    job.experienceLevel === experienceFilter;

    return matchesSearch && matchesLocation && matchesJobType && matchesExperience;
  });

  // Sort jobs
  const sortedJobs = [...filteredJobs].sort((a, b) => {
    switch (sortBy) {
      case 'date':
        return new Date(b.postedDate).getTime() - new Date(a.postedDate).getTime();
      case 'salary':
        // Simple salary comparison based on first number in salary string
        const aSalary = parseInt(a.salary.replace(/\D/g, ''));
        const bSalary = parseInt(b.salary.replace(/\D/g, ''));
        return bSalary - aSalary;
      case 'company':
        return a.company.localeCompare(b.company);
      case 'title':
        return a.title.localeCompare(b.title);
      default:
        // Relevance: featured jobs first, then by date
        if (a.isFeatured && !b.isFeatured) return -1;
        if (!a.isFeatured && b.isFeatured) return 1;
        return new Date(b.postedDate).getTime() - new Date(a.postedDate).getTime();
    }
  });

  if (!isHydrated || isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded mb-4"></div>
          <div className="h-32 bg-muted rounded mb-4"></div>
          <div className="h-32 bg-muted rounded mb-4"></div>
          <div className="h-32 bg-muted rounded mb-4"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <Icon name="ExclamationTriangleIcon" size={48} className="mx-auto text-destructive mb-4" />
        <h3 className="font-heading text-xl font-bold text-foreground mb-2">Error Loading Jobs</h3>
        <p className="text-text-secondary mb-4">{error}</p>
        <button
          onClick={loadJobsData}
          className="px-6 py-2 bg-primary text-primary-foreground rounded-industrial font-heading font-bold uppercase tracking-wide hover:bg-accent transition-industrial"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Top Banner Advertisement */}
      <AdZone zone="top_banner" className="h-24 w-full rounded-industrial" />

      {/* Authentication Status Banner */}
      {!isAuthenticated && (
        <div className="bg-primary/10 border border-primary/20 rounded-industrial p-4">
          <div className="flex items-center space-x-3">
            <Icon name="InformationCircleIcon" size={20} className="text-primary" />
            <div className="flex-1">
              <p className="text-foreground font-medium">
                <strong>Register to apply for machinery jobs</strong>
              </p>
              <p className="text-text-secondary text-sm">
                Create your profile to apply for positions and get job alerts from top employers.
              </p>
            </div>
            <button
              onClick={() => setIsRegistrationModalOpen(true)}
              className="bg-primary text-primary-foreground px-4 py-2 rounded-industrial font-heading font-bold uppercase tracking-wide text-sm hover:bg-accent transition-industrial"
            >
              Register Now
            </button>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <JobFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        locationFilter={locationFilter}
        onLocationChange={setLocationFilter}
        jobTypeFilter={jobTypeFilter}
        onJobTypeChange={setJobTypeFilter}
        experienceFilter={experienceFilter}
        onExperienceChange={setExperienceFilter}
        salaryFilter={salaryFilter}
        onSalaryChange={setSalaryFilter}
      />

      {/* Sort Controls */}
      <JobSortControls
        sortBy={sortBy}
        onSortChange={setSortBy}
        resultCount={sortedJobs.length}
      />

      {/* Jobs List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {sortedJobs.length > 0 ? (
          sortedJobs.map((job, index) => (
            <div key={job.id} className="flex flex-col">
              <JobCard
                job={job}
                isAuthenticated={isAuthenticated}
                onApplyClick={handleApplyClick}
              />
              
              {/* Inline Ad every 6 jobs (2 rows in 3-col layout) */}
              {(index + 1) % 6 === 0 && index < sortedJobs.length - 1 && (
                <div className="col-span-full mt-6">
                  <AdZone zone="jobs_inline" className="h-32 rounded-industrial" />
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12">
            <Icon name="BriefcaseIcon" size={48} className="mx-auto text-text-secondary mb-4" />
            <h3 className="font-heading text-xl font-bold text-foreground mb-2">No Jobs Found</h3>
            <p className="text-text-secondary mb-4">
              Try adjusting your search criteria or check back later for new opportunities.
            </p>
            <button
              onClick={() => {
                setSearchTerm('');
                setLocationFilter('All Locations');
                setJobTypeFilter('All Types');
                setExperienceFilter('All Levels');
                setSalaryFilter('All Ranges');
              }}
              className="px-6 py-2 bg-primary text-primary-foreground rounded-industrial font-heading font-bold uppercase tracking-wide hover:bg-accent transition-industrial"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>

      {/* Bottom Advertisement */}
      <AdZone zone="bottom_banner" className="h-32 w-full rounded-industrial" />

      {/* Registration Modal */}
      <RegistrationModal
        isOpen={isRegistrationModalOpen}
        onClose={() => setIsRegistrationModalOpen(false)}
        title="Apply for Jobs"
        description="Create your profile to apply for machinery and construction jobs. Get instant access to application forms and job alerts."
        actionText="Register to Apply"
        onSuccess={handleRegistrationSuccess}
      />
    </div>
  );
};

export default JobsBoardInteractive;